// A régua visual do pipeline de licitações (02/10/2026) — pura, testada no CI.
//
// Numa licitação quem manda é o calendário do ÓRGÃO, não o nosso: o cartão
// mostra o PRÓXIMO PRAZO (vermelho ≤ 3 dias, âmbar ≤ 7), não "dias sem
// movimento". A conta de dias vem de `classificarPrazoEdital` (prazo.ts), a
// mesma da lista de Oportunidades — uma régua, não três (a terceira era o
// retrato congelado de `data_abertura` no pipeline privado).
//
// Prazos são hora de PAREDE de Brasília sem offset ('AAAA-MM-DDTHH:MM:SS');
// `new Date(s)` lê isso como hora local, que é a certa para quem usa o ERP.
import { classificarPrazoEdital, type PrazoEditalClassificado } from '../prazo';
import { ABERTAS, ETAPAS, TERMINAIS, ehAberta, normalizar, propostaJaFoi } from './etapas';

export interface LicitacaoPipeline {
  id: number;
  numero_edital: string | null;
  numero_processo?: string | null;
  orgao: string | null;
  uf: string | null;
  cidade: string | null;
  modalidade: string | null;
  objeto: string | null;
  valor_estimado: number | string | null;
  preco_proposto?: number | string | null;
  preco_vencedor?: number | string | null;
  vencedor?: string | null;
  valor_contratado?: number | string | null;
  fit_score: number | null;
  fit_tecnico_nivel?: string | null;
  fit_tecnico_justificativa?: string | null;
  fit_resumo?: string | null;
  area_tecnica?: string | null;
  status_interno: string;
  etapa_desde: string | null;
  responsavel_email: string | null;
  proposta_id: number | null;
  fonte?: string | null;
  situacao?: string | null;
  link_edital?: string | null;
  conlic_url?: string | null;
  portal?: string | null;
  decisao_participar?: string | null;
  decisao_motivo?: string | null;
  decisao_em?: string | null;
  motivo_perda?: string | null;
  eventos_unack_count?: number | null;
  ultimo_evento_tipo?: string | null;
  ultimo_evento_resumo?: string | null;
  ultima_alteracao_em?: string | null;
  data_abertura: string | null;
  prazo_efetivo: string | null;
  proximo_prazo?: string | null;
  proximo_prazo_tipo?: string | null;
  checklist_total?: number | null;
  checklist_completos?: number | null;
  checklist_status?: string | null;
  /** "Enviamos a proposta" (05/10/2026): parede BRT, sem offset. */
  proposta_enviada_em?: string | null;
  proposta_enviada_por?: string | null;
  proposta_protocolo?: string | null;
  /** Acompanhamento pelo PNCP (licitacoes.acompanhamento_fonte). */
  acomp_referencia?: string | null;
  acomp_verificado_em?: string | null;
  acomp_ok_em?: string | null;
  acomp_erro?: string | null;
  acomp_situacao?: string | null;
  /** A mesma licitação pelas duas fontes (07/10/2026): o mais atualizado do grupo. */
  valor_unificado?: number | string | null;
  situacao_unificada?: string | null;
  situacao_unificada_fonte?: string | null;
  gemeas?: Array<{ id: number; fonte: string | null }> | null;
  importado_em?: string;
  atualizado_em?: string;
}

export const ROTULO_PRAZO: Record<string, string> = {
  proposta: 'proposta',
  sessao: 'sessão',
  impugnacao: 'impugnação',
  esclarecimento: 'esclarecimento',
  visita_tecnica: 'visita técnica',
  diligencia: 'diligência',
  recurso_intencao: 'intenção de recurso',
  recurso_razoes: 'razões de recurso',
  contrarrazoes: 'contrarrazões',
  assinatura: 'assinatura',
  garantia: 'garantia',
  outro: 'prazo',
};

export interface ProximoPrazo {
  tipo: string;
  rotulo: string;
  data: string;
  classe: PrazoEditalClassificado;
}

/**
 * O que o ÓRGÃO fez com a licitação (07/10/2026). Lucas: "se a licitação está
 * suspensa, como é o caso da SELIC, bacia do rio Gravataí, ela não deveria
 * constar como 'proposta vencida'". A #30879 estava Suspensa no PNCP desde
 * 02/10 e o cartão contava o prazo de 05/10 como vencido.
 *
 * A situação mais fresca é a do acompanhamento pelo PNCP (`acomp_situacao`,
 * lida todo dia); a de importação (`situacao`) fica para quem não é acompanhada.
 * No ConLicitação a situação vem em código (SUS, REVO…), no PNCP em texto.
 * Suspensa = o prazo PARA (o órgão marca outro ao reabrir). Revogada, anulada,
 * cancelada, deserta ou fracassada = acabou; quem decide é a pessoa (Perdido).
 */
export interface SituacaoDoOrgao {
  tipo: 'suspensa' | 'encerrada';
  texto: string;
  cor: string;
  /** Como veio da fonte ("Suspensa", "SUS", "Revogada"…). */
  bruta: string;
}

const ENCERRADAS: Array<[RegExp, string]> = [
  [/revogad|^revo$/i, 'revogada pelo órgão'],
  [/anulad|^anula$/i, 'anulada pelo órgão'],
  [/cancelad|^cance$/i, 'cancelada pelo órgão'],
  [/desert/i, 'licitação deserta'],
  [/fracassad/i, 'licitação fracassada'],
];

export function situacaoDoOrgao(l: Pick<LicitacaoPipeline, 'situacao' | 'acomp_situacao'> & { situacao_unificada?: string | null }): SituacaoDoOrgao | null {
  // Juntada com a outra fonte (07/10/2026): vale a que estiver FORA DO NORMAL
  // entre a do grupo (o banco já põe a anormal na frente) e a leitura diária
  // do PNCP — pegar só a primeira não vazia deixava "Divulgada" do grupo
  // esconder uma suspensão recém-lida (a SELIC do Gravataí de volta a
  // "vencida"). Sozinha, a régua de antes: o acompanhamento, mais novo, vence
  // a importação (ela não volta a olhar edital com prazo vencido).
  const leituras = l.situacao_unificada
    ? [l.situacao_unificada, l.acomp_situacao]
    : [l.acomp_situacao || l.situacao];
  for (const s of leituras) {
    const bruta = String(s || '').trim();
    if (!bruta) continue;
    if (/suspens|^suss?$/i.test(bruta)) return { tipo: 'suspensa', texto: 'suspensa pelo órgão', cor: '#B07500', bruta };
    for (const [re, texto] of ENCERRADAS) if (re.test(bruta)) return { tipo: 'encerrada', texto, cor: '#B91626', bruta };
  }
  return null;
}

/** O porquê do selo "suspensa/revogada pelo órgão", com o prazo que ficou parado. */
export function dicaDoOrgao(o: { tipo: string; bruta: string }, prazoProposta?: string | null): string {
  const m = String(prazoProposta || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  const quando = m ? ` (${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]})` : '';
  return o.tipo === 'suspensa'
    ? `Situação da fonte: ${o.bruta}. O prazo da proposta${quando} ficou parado — quando o órgão reabrir, a nova data chega pelo acompanhamento e aparece aqui.`
    : `Situação da fonte: ${o.bruta}. Confira o aviso do órgão e mova para Perdido se a licitação acabou.`;
}

/** A proposta já foi? Da Sessão em diante, ou com o envio registrado (05/10/2026). */
export const propostaEnviada = (l: Pick<LicitacaoPipeline, 'status_interno' | 'proposta_enviada_em'>) =>
  propostaJaFoi(l.status_interno, l.proposta_enviada_em);

/** "proposta enviada 01/10 08:10" — só quando o envio foi REGISTRADO (o
 *  cartão em Habilitação sem registro não inventa data). */
export function rotuloEnvio(l: Pick<LicitacaoPipeline, 'proposta_enviada_em'>): string | null {
  const m = String(l.proposta_enviada_em || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return m ? `proposta enviada ${m[3]}/${m[2]} ${m[4]}:${m[5]}` : null;
}

/**
 * O próximo prazo de uma licitação: o mais próximo entre o da tabela `prazos`
 * (já filtrado pelo servidor para "em aberto e no futuro") e o prazo da
 * proposta (`prazo_efetivo`). Vencido só aparece quando NÃO há nenhum à frente
 * — é o "prazo venceu sem registro de envio" do cartão. Com a proposta enviada
 * (Sessão em diante, ou envio registrado), o prazo dela sai da conta: era o que
 * fazia Santa Cruz do Sul, já em Habilitação, aparecer "proposta vencida".
 * Licitação suspensa (ou encerrada) pelo órgão também não tem prazo vencido:
 * o que passou ficou parado — só um prazo à FRENTE ainda aparece.
 */
export function proximoPrazo(l: LicitacaoPipeline, agora?: number): ProximoPrazo | null {
  const candidatos: { tipo: string; data: string }[] = [];
  if (l.proximo_prazo) candidatos.push({ tipo: l.proximo_prazo_tipo || 'outro', data: l.proximo_prazo });
  if (l.prazo_efetivo && !propostaEnviada(l)) candidatos.push({ tipo: 'proposta', data: l.prazo_efetivo });
  const classificados = candidatos
    .map(c => ({ ...c, classe: classificarPrazoEdital(c.data, agora) }))
    .filter((c): c is { tipo: string; data: string; classe: PrazoEditalClassificado } => !!c.classe);
  if (!classificados.length) return null;
  const futuros = classificados.filter(c => !c.classe.vencida).sort((a, b) => a.data.localeCompare(b.data));
  if (!futuros.length && situacaoDoOrgao(l)) return null;
  const escolhido = futuros[0] || classificados.sort((a, b) => b.data.localeCompare(a.data))[0];
  return { tipo: escolhido.tipo, rotulo: ROTULO_PRAZO[escolhido.tipo] || escolhido.tipo, data: escolhido.data, classe: escolhido.classe };
}

/** Dias desde que entrou na etapa (null quando não se sabe). */
export function diasNaEtapa(l: LicitacaoPipeline, agora: Date = new Date()): number | null {
  if (!l.etapa_desde) return null;
  const d = new Date(l.etapa_desde);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((agora.getTime() - d.getTime()) / 86400000));
}

/** Prontidão da habilitação: "3/12" ou null quando o checklist não existe. */
export function prontidao(l: LicitacaoPipeline): { completos: number; total: number; texto: string; pronta: boolean } | null {
  const total = Number(l.checklist_total) || 0;
  if (!total) return null;
  const completos = Number(l.checklist_completos) || 0;
  return { completos, total, texto: `${completos}/${total}`, pronta: completos >= total };
}

export const valorDaLicitacao = (l: LicitacaoPipeline) =>
  Number(normalizar(l.status_interno) === 'ganho' && l.valor_contratado != null ? l.valor_contratado
    : (l.valor_unificado ?? l.valor_estimado)) || 0;

export type Sinal = '' | 'vencendo' | 'alteracoes' | 'sem_responsavel' | 'vencido';

/** Os atalhos de cobrança do quadro. Só valem para etapa aberta. */
export function casaSinal(l: LicitacaoPipeline, sinal: Sinal, agora?: number): boolean {
  if (!sinal) return true;
  if (!ehAberta(l.status_interno)) return false;
  if (sinal === 'alteracoes') return Number(l.eventos_unack_count) > 0;
  if (sinal === 'sem_responsavel') return !String(l.responsavel_email || '').trim();
  const p = proximoPrazo(l, agora);
  if (sinal === 'vencido') return !!p && p.classe.vencida;
  if (sinal === 'vencendo') return !!p && !p.classe.vencida && p.classe.dias <= 7;
  return true;
}

export interface EtapaDoQuadro { codigo: string; qtd: number; valor: number }
export interface ResumoQuadro {
  etapas: EtapaDoQuadro[];
  terminais: EtapaDoQuadro[];
  abertas: { qtd: number; valor: number };
  sinais: { vencendo: number; alteracoes: number; sem_responsavel: number; vencido: number };
}

export function resumoDoQuadro(lista: LicitacaoPipeline[], agora?: number): ResumoQuadro {
  const por: Record<string, EtapaDoQuadro> = {};
  for (const e of [...ETAPAS, ...TERMINAIS]) por[e.codigo] = { codigo: e.codigo, qtd: 0, valor: 0 };
  const sinais = { vencendo: 0, alteracoes: 0, sem_responsavel: 0, vencido: 0 };
  for (const l of lista || []) {
    const c = normalizar(l.status_interno);
    if (!por[c]) continue;
    por[c].qtd++;
    por[c].valor += valorDaLicitacao(l);
    if (ehAberta(c)) {
      if (casaSinal(l, 'vencendo', agora)) sinais.vencendo++;
      if (casaSinal(l, 'alteracoes', agora)) sinais.alteracoes++;
      if (casaSinal(l, 'sem_responsavel', agora)) sinais.sem_responsavel++;
      if (casaSinal(l, 'vencido', agora)) sinais.vencido++;
    }
  }
  const etapas = ETAPAS.map(e => por[e.codigo]);
  const abertas = etapas.reduce((a, e) => ({ qtd: a.qtd + e.qtd, valor: a.valor + e.valor }), { qtd: 0, valor: 0 });
  return { etapas, terminais: TERMINAIS.map(e => por[e.codigo]), abertas, sinais };
}

export type Ordenacao = 'prazo' | 'valor' | 'recentes';

/** Ordem dos cartões na coluna: por prazo (o que vence antes em cima), valor ou movimento. */
export function ordenarLicitacoes<T extends LicitacaoPipeline>(lista: T[], modo: Ordenacao, agora?: number): T[] {
  const copia = [...lista];
  if (modo === 'valor') return copia.sort((a, b) => valorDaLicitacao(b) - valorDaLicitacao(a));
  if (modo === 'recentes') return copia.sort((a, b) => String(b.etapa_desde || '').localeCompare(String(a.etapa_desde || '')));
  const chave = (l: LicitacaoPipeline) => {
    const p = proximoPrazo(l, agora);
    // Sem prazo vai para o fim; vencido vai depois dos futuros (já perdeu a vez).
    if (!p) return '9999-99-99';
    return (p.classe.vencida ? '9998-' : '') + p.data;
  };
  return copia.sort((a, b) => chave(a).localeCompare(chave(b)));
}

/** Para a trilha: índice da etapa atual entre as abertas (-1 fora delas). */
export const indiceDaEtapa = (s?: string | null) => ABERTAS.indexOf(normalizar(s));
